/**
 * The calculations carried over from the design prototype, unchanged in
 * behaviour. See docs/architecture.md §5 and the `Component` class in
 * design/project/Bar OS.dc.html.
 *
 * These live here rather than in the API or the SPA because both need them
 * and they must agree exactly — a cost shown on the tablet and a cost stored
 * against an order that differ by a cent is a support call.
 */
/** Ethanol density, g/mL. Used for standard drinks. */
const ETHANOL_DENSITY = 0.789;
/** A standard drink in Australia is 10 g of ethanol. */
const GRAMS_PER_STANDARD_DRINK = 10;
/** Australian GST. Prices are inclusive; gross profit is calculated ex-GST. */
const GST_MULTIPLIER = 1.1;
/**
 * Cost, ABV and standard drinks for a build.
 *
 * Dilution lines (shake, stir, cracked ice) carry volume but no cost and no
 * alcohol. They must be included: they are what takes a 95 mL build to 115 mL
 * in the glass, and excluding them overstates ABV by roughly a fifth.
 */
export function buildTotals(lines, sundries = 0) {
    const volumeMl = lines.reduce((total, line) => total + line.ml, 0);
    const cost = lines.reduce((total, line) => total + line.ml * line.costPerMl, sundries);
    if (volumeMl === 0) {
        return { volumeMl: 0, cost, abv: 0, standardDrinks: 0 };
    }
    const abv = lines.reduce((total, line) => total + line.ml * line.abv, 0) / volumeMl;
    return {
        volumeMl,
        cost,
        abv,
        standardDrinks: standardDrinks(volumeMl, abv),
    };
}
/** Australian standard drinks for a volume at a given ABV. */
export function standardDrinks(volumeMl, abv) {
    return (volumeMl * (abv / 100) * ETHANOL_DENSITY) / GRAMS_PER_STANDARD_DRINK;
}
/**
 * Gross profit percentage.
 *
 * The menu price includes GST; cost does not. Comparing them directly would
 * overstate GP by about nine points, so the price is taken ex-GST first.
 */
export function grossProfit(priceIncGst, cost) {
    const exGst = priceIncGst / GST_MULTIPLIER;
    if (exGst === 0)
        return 0;
    return ((exGst - cost) / exGst) * 100;
}
// ------------------------------------------------------------- stocktaking
/**
 * Convert a scale reading to millilitres held.
 *
 * The bottle on the scale includes the glass, so tare comes off first; the
 * remainder is converted by the liquid's own density. Syrup at 1.28 and gin
 * at 0.94 differ by more than a third, which is why density is per ingredient
 * rather than assumed.
 */
export function weighToMl(grams, tareKg, density) {
    const netKg = Math.max(0, grams / 1000 - tareKg);
    return (netKg / density) * 1000;
}
/** Convert a by-eye count — whole bottles plus tenths — to millilitres. */
export function tenthsToMl(wholeBottles, tenths, bottleMl) {
    return wholeBottles * bottleMl + (tenths * bottleMl) / 10;
}
/**
 * Variance of a count against what the system expected to be there.
 *
 * Thresholds match the prototype: under 3% is normal pouring tolerance,
 * under 8% is worth a look, beyond that something is wrong.
 */
export function variance(countedMl, expectedMl) {
    if (expectedMl === 0) {
        return {
            deltaMl: countedMl,
            percent: 0,
            band: countedMl === 0 ? "ok" : "investigate",
        };
    }
    const deltaMl = countedMl - expectedMl;
    const percent = (deltaMl / expectedMl) * 100;
    const magnitude = Math.abs(percent);
    const band = magnitude < 3 ? "ok" : magnitude < 8 ? "watch" : "investigate";
    return { deltaMl, percent, band };
}
// ------------------------------------------------------------------- cost
/**
 * Resolve an ingredient's cost per millilitre for one venue.
 *
 * The org sets a default — one rep, one trade price, true for a single-city
 * group. A venue may override it where that genuinely differs: a group with
 * venues interstate buys from different distributors at different prices.
 *
 * Same fallback shape as the menu price override, deliberately. `null` and
 * `undefined` both mean "no override" — a venue override of `0` is honoured,
 * because a free house-made ingredient is a real thing and `??` would keep it
 * where `||` would silently fall back to the org price.
 */
export function resolveCostPerMl(orgCostPerMl, venueOverride) {
    return venueOverride ?? orgCostPerMl;
}
// ---------------------------------------------------------------- currency
/** Round to whole cents. Money is compared and stored, never left as float dust. */
export function toCents(dollars) {
    return Math.round(dollars * 100);
}
/** Format for display: `$1,284.00`, `-$41.20`. */
export function money(dollars) {
    const [whole = "0", fraction = "00"] = Math.abs(dollars)
        .toFixed(2)
        .split(".");
    const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return `${dollars < 0 ? "-$" : "$"}${grouped}.${fraction}`;
}
//# sourceMappingURL=calc.js.map